import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { FadeUp, rich, Magnetic } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contact.form.success, {
        description: t.contact.form.successSub,
        style: { background: "#1A1A19", color: "#F2F0E9", border: "1px solid #2A2A28" },
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error, {
        style: { background: "#1A1A19", color: "#F2F0E9", border: "1px solid #2A2A28" },
      });
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: MapPin, label: t.contact.info.showroomLabel, value: t.contact.info.showroom, testid: "contact-info-address" },
    { icon: Phone, label: t.contact.info.phoneLabel, value: t.contact.info.phone, href: `tel:${t.contact.info.phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
    { icon: Mail, label: t.contact.info.emailLabel, value: t.contact.info.email, href: `mailto:${t.contact.info.email}`, testid: "contact-info-email" },
    { icon: Clock, label: t.contact.info.hoursLabel, value: t.contact.info.hours, testid: "contact-info-hours" },
  ];

  return (
    <section id="contatti" data-testid="contact-section" className="py-28 md:py-40 bg-surface/40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <FadeUp>
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-12 bg-brass" />
                <p className="eyebrow">{t.contact.overline}</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-cream">
                {rich(t.contact.title)}
              </h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mt-6 text-stone font-light leading-relaxed max-w-md">{t.contact.sub}</p>
            </FadeUp>
          </div>
          <motion.div
            className="hidden lg:block overflow-hidden self-start"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            whileInView={{ clipPath: "inset(0 0 0% 0)" }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src="https://images.unsplash.com/photo-1762617661483-69977c02231a?q=80&w=1600&auto=format&fit=crop"
              alt="Showroom Green Gate"
              className="w-full h-64 object-cover"
              data-hover
            />
          </motion.div>
        </div>

        <FadeUp delay={0.2}>
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-x-16 lg:gap-x-24 gap-y-8"
          >
            <div className="order-1 lg:order-none lg:col-start-1 lg:row-start-1 flex flex-col justify-end border-b border-line">
              <div className="grid sm:grid-cols-2 gap-8">
                <input
                  data-testid="contact-input-name"
                  className="gg-input"
                  placeholder={t.contact.form.name}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  maxLength={120}
                />
                <input
                  data-testid="contact-input-email"
                  className="gg-input"
                  type="email"
                  placeholder={t.contact.form.email}
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  maxLength={180}
                />
              </div>
            </div>
            <div className="order-2 lg:order-none lg:col-start-1 lg:row-start-2 flex flex-col justify-end border-b border-line">
              <input
                data-testid="contact-input-phone"
                className="gg-input"
                type="tel"
                placeholder={t.contact.form.phone}
                name="phone"
                value={form.phone}
                onChange={onChange}
                maxLength={40}
              />
            </div>
            <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-3 flex flex-col justify-end border-b border-line">
              <textarea
                data-testid="contact-input-message"
                className="gg-input resize-none"
                rows={4}
                placeholder={t.contact.form.message}
                name="message"
                value={form.message}
                onChange={onChange}
                required
                maxLength={4000}
              />
            </div>
            <div className="order-4 lg:order-none lg:col-start-1 lg:row-start-4">
              <Magnetic className="inline-block">
                <button
                  data-testid="contact-form-submit"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 border border-brass text-brass text-[13px] uppercase tracking-[0.16em] font-semibold rounded-full px-9 py-4 hover:bg-brass hover:text-ink disabled:opacity-60 transition-colors duration-400"
                >
                  {sending ? t.contact.form.sending : t.contact.form.submit}
                  <ArrowUpRight size={16} strokeWidth={1.75} />
                </button>
              </Magnetic>
            </div>

            <div className="order-5 lg:order-none lg:col-start-2 lg:row-start-1 flex flex-col justify-end border-b border-line lg:pl-12">
              <div className="flex items-start gap-5 py-6" data-testid="contact-info-address">
                <MapPin size={20} strokeWidth={1.25} className="text-brass mt-1 shrink-0" />
                <div>
                  <p className="eyebrow text-[10px] text-stone/70">{info[0].label}</p>
                  <p className="mt-2 text-cream font-light whitespace-pre-line">{info[0].value}</p>
                </div>
              </div>
            </div>
            <div className="order-6 lg:order-none lg:col-start-2 lg:row-start-2 flex flex-col justify-end border-b border-line lg:pl-12">
              <div className="flex items-start gap-5 py-6" data-testid="contact-info-phone">
                <Phone size={20} strokeWidth={1.25} className="text-brass mt-1 shrink-0" />
                <div>
                  <p className="eyebrow text-[10px] text-stone/70">{info[1].label}</p>
                  <a href={info[1].href} className="block mt-2 text-cream font-light hover:text-brass transition-colors duration-300">
                    {info[1].value}
                  </a>
                </div>
              </div>
            </div>
            <div className="order-7 lg:order-none lg:col-start-2 lg:row-start-3 flex flex-col justify-end border-b border-line lg:pl-12">
              <div className="flex items-start gap-5 py-6" data-testid="contact-info-email">
                <Mail size={20} strokeWidth={1.25} className="text-brass mt-1 shrink-0" />
                <div>
                  <p className="eyebrow text-[10px] text-stone/70">{info[2].label}</p>
                  <a href={info[2].href} className="block mt-2 text-cream font-light hover:text-brass transition-colors duration-300">
                    {info[2].value}
                  </a>
                </div>
              </div>
            </div>
            <div className="order-8 lg:order-none lg:col-start-2 lg:row-start-4 flex flex-col justify-end lg:pl-12">
              <div className="flex items-start gap-5 py-6" data-testid="contact-info-hours">
                <Clock size={20} strokeWidth={1.25} className="text-brass mt-1 shrink-0" />
                <div>
                  <p className="eyebrow text-[10px] text-stone/70">{info[3].label}</p>
                  <p className="mt-2 text-cream font-light whitespace-pre-line">{info[3].value}</p>
                </div>
              </div>
            </div>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
